import {
  NodeTypes,
  type ApiNode,
  type Filter,
  type FilterDataType,
} from "@/workflow_api/types/workflow";
import type { Variable } from "../../types";
import type { ScopeState } from "../components/scope-selector";
import type { DCConditionData } from "../components/condition";
import type { LogicalOperator } from "../../if/types";
import type { ActionItemType, ValueColumn, ValueInputType } from "../types";
import { formatDate } from "@/utils/format";
import type { VariableType } from "../../calculated-field/CalculatedFieldOperation/types";

export const getSerializedScope = (scope: ScopeState): Array<Filter> => {
  const condition = scope.conditionScope;
  const isDateType = scope.scope.data_type === "date";
  const comparative = getSerializedVariableValue(
    scope.comparativeValue,
    isDateType,
  );
  const initRange = getSerializedVariableValue(
    scope.initialRange.value,
    isDateType,
    false,
  );
  const endRange = getSerializedVariableValue(
    scope.endRange.value,
    isDateType,
    false,
  );
  const serializedScope: Filter = {
    condition: condition.name ?? "",
    name: scope.scope.id,
    type: (scope.scope.data_type as FilterDataType) ?? "DATE",
    value: condition?.needsComparativeValue
      ? !comparative.isVariable
        ? comparative.value
        : null
      : condition?.needsComparativeRange
        ? JSON.stringify([initRange.value, endRange.value])
        : null,
  };
  if (comparative.isVariable) {
    const variable = scope.comparativeValue as Variable;
    if (variable.global_type) {
      serializedScope.value = variable.node_id || null;
      serializedScope.global_type = variable.global_type;
    } else {
      serializedScope.connection = variable.node_id;
      serializedScope.field = variable.id;
    }
  }

  return [serializedScope];
};

const getSerializedVariableValue = (
  value: string | Variable | null,
  shouldNormalizeDate: boolean = false,
  useNameAsValue: boolean = true,
): {
  isVariable: boolean;
  value: string | null;
  type: VariableType | null;
} => {
  if (value === null) {
    return {
      isVariable: false,
      value: null,
      type: null,
    };
  }

  if (typeof value === "string") {
    return {
      isVariable: false,
      value: shouldNormalizeDate ? formatDate(value) : value,
      type: null,
    };
  }
  return {
    isVariable: true,
    value: useNameAsValue ? (value as Variable).name : (value as Variable).id,
    type: (value as Variable).type,
  };
};

const getFilterType = (
  operationType?: ValueInputType | VariableType,
): FilterDataType => {
  switch (operationType) {
    case "positive-integer":
    case "number":
      return "NUMBER";
    case "boolean":
      return "BOOLEAN";
    case "date":
      return "DATE";
    case "string":
    case "regex":
    default:
      return "STRING";
  }
};

const getFilterValue = (
  value: string | null,
  type: FilterDataType,
): string | number | boolean | null => {
  if (value === null) return null;
  if (type === "NUMBER") {
    return Number(value);
  } else if (type === "BOOLEAN") {
    return value === "true";
  } else {
    return value;
  }
};

export const getSerializedFilters = (
  conditionsData: DCConditionData[],
): Array<Filter> => {
  return conditionsData.map((condition) => {
    const isDateType = condition.operation?.type === "date";
    const variable = getSerializedVariableValue(condition.variable);
    const comparative = getSerializedVariableValue(
      condition.comparativeValue,
      isDateType,
      false,
    );
    const initRange = getSerializedVariableValue(
      condition.initialRangeValue,
      isDateType,
      false,
    );
    const endRange = getSerializedVariableValue(
      condition.endRangeValue,
      isDateType,
      false,
    );
    const type = getFilterType(
      variable?.type ??
        condition.operation?.comparativeValueType ??
        condition.operation?.type,
    );
    const filter = {
      name: variable.value || "",
      condition: condition.operation?.name ?? "",
      value: condition.operation?.needsComparativeValue
        ? !comparative.isVariable
          ? getFilterValue(comparative.value, type)
          : null
        : condition.operation?.needsComparativeRange
          ? JSON.stringify([initRange.value, endRange.value])
          : null,
      type,
    } as Filter;
    if (comparative.isVariable) {
      const variable = condition.comparativeValue as Variable;
      if (variable.global_type) {
        filter.value = variable.node_id || null;
        filter.global_type = variable.global_type;
      } else {
        if (
          variable.ref_name === NodeTypes.N8N ||
          variable.ref_name === NodeTypes.INTEROPERABILITY
        ) {
          filter.value = null;
          filter.output_name = variable.name;
          filter.connection = variable.node_id;
        } else if (variable.node_id === variable.id) {
          filter.value = null;
          filter.connection = variable.node_id;
          filter.field = variable.id;
        } else {
          filter.value = null;
          filter.connection = variable.node_id;
        }
      }
    }
    return filter;
  });
};

/**
 * Serialize the columns data for create and update operations
 *
 * @param columns
 * @returns Serialized columns as a JSON string in the format expected by the API
 */
export const getSerializedColumns = (columns: ValueColumn[]): string => {
  const columnsData: Record<string, string | boolean> = {};
  columns.forEach((column) => {
    if (column.value !== "") {
      const serializedValue = getSerializedVariableValue(
        column.value,
        column.valueType === "date",
      );
      if (column.valueType === "boolean") {
        columnsData[column.label] = column.value === "true";
      } else {
        // Si es una variable, usar el valor de la variable, si no, usar el valor directo
        columnsData[column.label] = serializedValue.isVariable
          ? `{{${serializedValue.value}}}` // Formato para variables
          : serializedValue.value || "";
      }
    }
  });

  return JSON.stringify(columnsData);
};

/**
 * Serialize the node to be saved in the db
 * Data Center Node
 *
 * @param currentNode
 * @param title
 * @param flowId
 * @param tableName
 * @param is_multiple
 * @param conditions
 * @returns Update body API node
 */
export const getSerializedNode = (
  nodeType: ActionItemType,
  currentNode: ApiNode,
  title: string,
  flowId: string,
  tableName: string,
  tableSource: string,
  is_multiple: boolean,
  conditions: DCConditionData[],
  logicalOperator: LogicalOperator,
  scope: ScopeState,
  outputName: string,
  limitCount: number,
  columns: ValueColumn[],
  manual_continue?: boolean,
  executeQuery?: boolean,
) => {
  return {
    node_id: currentNode.id!,
    node: {
      display_name: title,
      flow_id: flowId,
      order: currentNode.order,
      position_x: currentNode.position_x,
      position_y: currentNode.position_y,
      ref_name: currentNode.ref_name,
      type: currentNode.type,
      manual_continue: manual_continue ?? false,
      inputs: [
        {
          name: "operation",
          type: "STRING",
          value: nodeType.toUpperCase(),
        },
        {
          name: "table",
          type: "STRING",
          value: tableName,
        },
        {
          name: "source",
          type: "STRING",
          value: tableSource.toUpperCase(),
        },
        {
          name: "data",
          type: "ARRAY",
          value:
            nodeType === "create" || nodeType === "update"
              ? getSerializedColumns(columns)
              : [],
        },
        {
          name: "execute_query",
          type: "BOOLEAN",
          value: executeQuery || false,
        },
        {
          name: "filters",
          type: "FILTER",
          value: null,
          filters:
            nodeType !== "create" ? getSerializedFilters(conditions) : [],
        },
        {
          name: "is_multiple",
          type: "BOOLEAN",
          value: is_multiple,
        },
        {
          name: "limit",
          type: "NUMBER",
          value: nodeType !== "create" ? limitCount : 10,
        },
        {
          name: "scope",
          type: "STRING",
          value: JSON.stringify(getSerializedScope(scope)),
        },
        {
          name: "logic_operator",
          type: "STRING",
          value: logicalOperator.toUpperCase(),
        },
        {
          name: "output_name",
          type: "STRING",
          value: outputName,
        },
      ],
    },
  };
};
